import { GscSite, GscRow, GscAnalysisResult, GscPrediction } from '../types';
import { runGeminiPrompt, cleanAndParseJson } from './geminiService';

// --- API CALLS (Yêu cầu Google OAuth Access Token) ---

export const fetchGscSites = async (accessToken: string): Promise<GscSite[]> => {
    try {
        const response = await fetch('https://www.googleapis.com/webmasters/v3/sites', {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });
        
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error?.message || 'Không thể tải danh sách website');
        }

        const data = await response.json();
        return data.siteEntry || [];
    } catch (error) {
        console.error("GSC Sites Error:", error);
        throw error;
    }
};

export const fetchGscData = async (
    accessToken: string, 
    siteUrl: string, 
    startDate: string, 
    endDate: string, 
    dimensions: string[] = ['query']
): Promise<GscRow[]> => {
    try {
        const response = await fetch(`https://www.googleapis.com/webmasters/v3/sites/${encodeURIComponent(siteUrl)}/searchAnalytics/query`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                startDate,
                endDate,
                dimensions,
                rowLimit: 100 // Lấy Top 100 dòng để AI phân tích hiệu quả nhất
            })
        });

        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error?.message || 'Không thể tải dữ liệu phân tích');
        }

        const data = await response.json();
        return data.rows || [];
    } catch (error) {
        console.error("GSC Data Error:", error);
        throw error;
    }
};

// --- AI ANALYSIS (Đã tối ưu Tiếng Việt 100%) ---

export const analyzeGscDataWithGemini = async (siteUrl: string, data: GscRow[]): Promise<GscAnalysisResult> => {
    // Chuyển đổi dữ liệu sang định dạng tối ưu cho Token AI
    const csvData = data.slice(0, 60).map(r => {
        const query = r.keys[0];
        const page = r.keys[1] || 'N/A';
        return `${query} | ${page} | Clicks:${r.clicks} | Imp:${r.impressions} | CTR:${(r.ctr * 100).toFixed(2)}% | Pos:${r.position.toFixed(1)}`;
    }).join('\n');

    const prompt = `
    VAI TRÒ: Chuyên gia tư vấn SEO cấp cao tại Việt Nam.
    NHIỆM VỤ: Phân tích dữ liệu từ Google Search Console cho website: ${siteUrl}.
    MỤC TIÊU: Đưa ra các hành động cụ thể để tăng Traffic và cải thiện thứ hạng.
    NGÔN NGỮ TRẢ VỀ: Tiếng Việt 100% (Trừ các thuật ngữ kỹ thuật giữ nguyên tiếng Anh như Volume, CTR, Cannibalization).

    DỮ LIỆU ĐẦU VÀO (Top Query & Landing Page):
    Định dạng: Từ khóa | URL bài viết | Clicks | Impressions | CTR | Vị trí trung bình
    ${csvData}

    YÊU CẦU ĐẦU RA (JSON):
    {
        "overallHealth": "Tóm tắt ngắn gọn (1-2 câu) tình trạng sức khỏe của website hiện tại bằng tiếng Việt",
        "insights": [
            {
                "type": "opportunity" | "decay" | "ctr" | "cannibalization",
                "title": "Tiêu đề hành động ngắn gọn bằng tiếng Việt",
                "description": "Giải thích vấn đề hoặc cơ hội một cách chi tiết, nêu rõ Từ khóa và URL bị ảnh hưởng bằng tiếng Việt",
                "impact": "High" | "Medium" | "Low",
                "action": "Lời khuyên cực kỳ cụ thể bằng tiếng Việt. VD: 'Sửa tiêu đề H1 thành X', 'Gộp bài A vào bài B', 'Bổ sung Schema FAQ'",
                "relatedUrls": ["URL liên quan"],
                "relatedQueries": ["Từ khóa liên quan"]
            }
        ]
    }

    QUY TẮC PHÂN TÍCH:
    1. **Cannibalization (Cực kỳ quan trọng):** Nếu có nhiều URL cùng xếp hạng cho 1 từ khóa, hãy cảnh báo ngay và yêu cầu gộp bài hoặc đặt Canonical.
    2. **Striking Distance:** Tìm các từ khóa ở Vị trí 4-20 có Impressions cao nhưng Clicks thấp. Gợi ý bổ sung nội dung chuyên sâu.
    3. **Tối ưu CTR:** Các bài ở Top 10 có Impressions cao nhưng CTR < 2%. Yêu cầu viết lại Title/Meta Description hấp dẫn hơn.
    4. **Mismatched Intent:** Nếu từ khóa "thông tin" lại dẫn về "trang sản phẩm" và có chỉ số tệ, gợi ý viết bài blog mới.

    Hạn chế kết quả ở 5-7 Insight quan trọng nhất.
    `;

    try {
        const response = await runGeminiPrompt(
            'gemini-4-argon',
            prompt,
            { responseMimeType: "application/json" }
        );

        return cleanAndParseJson<GscAnalysisResult>(response.text, { 
            overallHealth: "Không thể phân tích dữ liệu do lỗi cấu trúc AI.", 
            insights: [] 
        });
    } catch (e) {
        console.error("AI Analysis Error:", e);
        throw e;
    }
};

// --- NEW FUNCTION: GSC PREDICTIVE ANALYSIS ---
export const generateGscPredictions = async (siteUrl: string, data: GscRow[]): Promise<GscPrediction[]> => {
    const csvData = data.slice(0, 100).map(r => {
        const query = r.keys[0];
        const page = r.keys[1] || 'N/A';
        return `${query}|${page}|${r.clicks}|${r.impressions}|${(r.ctr * 100).toFixed(2)}|${r.position.toFixed(1)}`;
    }).join('\n');

    const prompt = `
    VAI TRÒ: Nhà khoa học dữ liệu & Chuyên gia dự báo SEO (Data Scientist & SEO Forecaster).
    NHIỆM VỤ: Phân tích dữ liệu Google Search Console cho website ${siteUrl} để tìm ra 3-5 cơ hội tăng trưởng tiềm năng nhất và đưa ra dự báo có thể định lượng.

    DỮ LIỆU ĐẦU VÀO (CSV format: Query|Page|Clicks|Impressions|CTR|Position):
    ${csvData}

    QUY TRÌNH TƯ DUY:
    1.  **Xác định 'Striking Distance' Keywords:** Tìm các từ khóa có Impressions cao (ví dụ > 500) và Vị trí trung bình từ 5 đến 20. Đây là những "mỏ vàng" dễ khai thác nhất.
    2.  **Ước tính Click tiềm năng:** Với mỗi từ khóa tiềm năng, ước tính số click tăng thêm mỗi tháng nếu nó lọt vào Top 3. Sử dụng công thức đơn giản: Potential Clicks = Impressions * (CTR mục tiêu - CTR hiện tại). Giả định CTR Top 3 trung bình là 12% (0.12).
    3.  **Xây dựng Dự báo:** Viết một câu dự báo cụ thể, bao gồm hành động cần làm.
    4.  **Đánh giá Độ tự tin:** Dựa trên lượng Impressions và Vị trí hiện tại, đưa ra mức độ tự tin (High, Medium, Low).

    YÊU CẦU ĐẦU RA (JSON):
    {
      "predictions": [
        {
          "keyword": "từ khóa tiềm năng",
          "page": "/url-lien-quan/",
          "prediction": "Từ khóa 'từ khóa tiềm năng' đang có Impressions rất cao nhưng thứ hạng chưa tốt. Dự báo có thể vào Top 5 trong 2 tháng tới nếu tối ưu lại H1 và bổ sung 500 từ nội dung chuyên sâu giải quyết các câu hỏi liên quan.",
          "confidence": "High",
          "potentialClicks": 500,
          "timeframe": "2 tháng"
        }
      ]
    }
    `;

    try {
        const response = await runGeminiPrompt(
            'gemini-4-argon',
            prompt,
            { responseMimeType: "application/json" }
        );
        const result = cleanAndParseJson<{ predictions: GscPrediction[] }>(response.text, { predictions: [] });
        return result.predictions;
    } catch (e) {
        console.error("AI Prediction Error:", e);
        throw e;
    }
};
